import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Play } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteWork4MeScript, getWork4MeScript, runWork4MeScript, updateWork4MeScript } from '@/api/scripts-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { JsonValue, ScriptRunOutcome } from '@vivport/backend/types';
import { toast } from 'sonner';
import { parseJsonText, Work4MeScriptForm } from './Work4MeScriptForm';

function prettyJson(value: unknown): string {
  return value === null || value === undefined ? '' : JSON.stringify(value, null, 2);
}

export function Work4MeScriptDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const parsedScriptId = Number(id);
  const isValidScriptId = Number.isInteger(parsedScriptId) && parsedScriptId > 0;

  const scriptQuery = useQuery({
    queryKey: ['work4me-script', parsedScriptId],
    queryFn: () => getWork4MeScript(parsedScriptId),
    enabled: isValidScriptId,
  });

  const script = scriptQuery.data;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [inputExampleText, setInputExampleText] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [runInputText, setRunInputText] = useState('');
  const [runInputError, setRunInputError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<ScriptRunOutcome | null>(null);

  useEffect(() => {
    if (!script) return;
    setName(script.name);
    setDescription(script.description ?? '');
    setBody(script.body);
    setInputExampleText(prettyJson(script.inputExample));
    setIsEnabled(script.isEnabled);
    setRunInputText(prettyJson(script.inputExample));
    setRunResult(null);
    setRunInputError(null);
  }, [script]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!isValidScriptId) throw new Error('Invalid script id');
      const parsed = parseJsonText(inputExampleText);
      if (!parsed.ok) throw new Error(`Input example is not valid JSON: ${parsed.message}`);
      return updateWork4MeScript(parsedScriptId, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        body,
        inputExample: parsed.value as JsonValue,
        isEnabled,
      });
    },
    onSuccess: async (updated) => {
      toast.success(`Saved "${updated.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['work4me-script', parsedScriptId] }),
        queryClient.invalidateQueries({ queryKey: ['work4me-scripts'] }),
      ]);
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to save script.'));
    },
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!isValidScriptId) throw new Error('Invalid script id');
      return deleteWork4MeScript(parsedScriptId);
    },
    onSuccess: async () => {
      toast.success('Script deleted.');
      await queryClient.invalidateQueries({ queryKey: ['work4me-scripts'] });
      navigate('/work4me/scripts');
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to delete script.'));
    },
  });

  const runMutation = useMutation({
    mutationFn: async (input: JsonValue) => runWork4MeScript(parsedScriptId, input),
    onSuccess: (outcome) => {
      setRunResult(outcome);
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to run script.'));
    },
  });

  function handleRun() {
    const parsed = parseJsonText(runInputText);
    if (!parsed.ok) {
      setRunInputError(parsed.message);
      return;
    }
    setRunInputError(null);
    runMutation.mutate(parsed.value as JsonValue);
  }

  const isDirty =
    Boolean(script)
    && (
      name.trim() !== (script?.name ?? '')
      || (description.trim() ? description.trim() : '') !== (script?.description ?? '')
      || body !== (script?.body ?? '')
      || inputExampleText !== prettyJson(script?.inputExample)
      || isEnabled !== script?.isEnabled
    );

  if (!isValidScriptId) {
    return (
      <div className="px-4 py-6">
        <p className="text-sm text-destructive">Invalid script id.</p>
      </div>
    );
  }

  if (scriptQuery.isLoading) {
    return (
      <div className="px-4 py-6">
        <p className="text-sm text-muted-foreground">Loading script...</p>
      </div>
    );
  }

  if (scriptQuery.isError || !script) {
    return (
      <div className="px-4 py-6">
        <p className="text-sm text-destructive">Unable to load script detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Script: {script.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{script.ownerScope}</Badge>
              <Badge variant={isEnabled ? 'default' : 'secondary'}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/scripts')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Scripts
              </Button>
              <Button type="button" variant="destructive" size="sm" onClick={() => setConfirmDeleteOpen(true)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete script</DialogTitle>
            <DialogDescription>Delete '{script.name}'? Neo and agents will no longer be able to run it.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Work4MeScriptForm
        name={name}
        description={description}
        body={body}
        inputExampleText={inputExampleText}
        isEnabled={isEnabled}
        isDirty={isDirty}
        isSaving={saveMutation.isPending}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onBodyChange={setBody}
        onInputExampleTextChange={setInputExampleText}
        onIsEnabledChange={setIsEnabled}
        onSave={() => saveMutation.mutate()}
      />

      <Separator className="my-4" />

      <div className="grid gap-4 px-4 pb-6">
        <h3 className="text-lg font-light tracking-wide">Run</h3>
        <div>
          <Label htmlFor="script-run-input">Input (JSON)</Label>
          <Textarea
            id="script-run-input"
            rows={8}
            value={runInputText}
            onChange={(event) => setRunInputText(event.target.value)}
            className="mt-1 font-mono text-xs"
            spellCheck={false}
          />
          {runInputError ? <p className="mt-1 text-xs text-destructive">Invalid JSON: {runInputError}</p> : null}
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" onClick={handleRun} className="gap-2" disabled={runMutation.isPending || !isEnabled}>
            <Play className="h-4 w-4" />
            {runMutation.isPending ? 'Running…' : 'Run'}
          </Button>
          {!isEnabled ? <p className="text-xs text-muted-foreground">Enable the script to run it.</p> : null}
          {runResult ? (
            <p className={`text-xs ${runResult.ok ? 'text-muted-foreground' : 'text-destructive'}`}>
              {runResult.ok
                ? `OK in ${runResult.durationMs} ms`
                : `${runResult.error.name}: ${runResult.error.message} (${runResult.durationMs} ms)`}
            </p>
          ) : null}
        </div>
        {runResult?.ok ? (
          <div>
            <p className="text-sm font-medium">Output</p>
            <pre className="mt-1 font-mono text-xs whitespace-pre-wrap break-words">{prettyJson(runResult.output)}</pre>
          </div>
        ) : null}
        {runResult && runResult.logs.length > 0 ? (
          <div>
            <p className="text-sm font-medium">Logs</p>
            <ul className="mt-1 space-y-0.5">
              {runResult.logs.map((line, index) => (
                <li key={`${index}-${line.slice(0, 16)}`} className="font-mono text-xs whitespace-pre-wrap break-words">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
